// Le mezze giornate lavorative della sequenza di chiamate (piano M5, D10; decisione del product owner del 28 settembre
// 2026, proposta 6 di «Impostazioni, le altre sezioni» e novità 9 e 12 della sequenza). Dal calendario dell'azienda
// (`CallbackSchedulingConfig`: fuso, orari della settimana, chiusure) e dalla regola della sequenza: la mattina
// dall'apertura alla fine della mattina, il pomeriggio dall'inizio del pomeriggio alla chiusura, il venerdì soltanto la
// mattina finché l'opzione è accesa, niente nei giorni chiusi. Una mezza giornata va dal suo inizio (compreso) alla sua
// fine (esclusa), come nel mock-up (`sequenceHalves`, `halfAt`, `halfAfter`, `workMinutes`, `firstCallDeadline` di
// `mockup.js`). I minuti sono quelli dell'orologio: secondi e millisecondi non contano.
import { instantFromLocal, isoWeekday, searchLimitDays, workingWindowOf } from "./callback-scheduling.js";
import { addLocalDays, dayString, localDateOf, requireTimeZone } from "./local-days.js";
const MINUTE_MS = 60_000;
const FRIDAY = 5;
function parseDay(day) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof day === "string" ? day : "");
    if (!match)
        throw new TypeError("day must use YYYY-MM-DD format");
    const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    if (!isCalendarDay(date, day))
        throw new RangeError("day is not a valid calendar date");
    return date;
}
/** Il giorno esiste nel calendario (niente 30 febbraio). */
function isCalendarDay(date, day) {
    return dayString(addLocalDays(date, 0)) === day;
}
function clockOf(value, label) {
    const match = /^(\d{2}):(\d{2})$/.exec(typeof value === "string" ? value : "");
    if (!match)
        throw new TypeError(`${label} must use HH:mm format`);
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59)
        throw new RangeError(`${label} is not a valid time`);
    return { hour, minute };
}
const later = (left, right) => (left.getTime() >= right.getTime() ? left : right);
const earlier = (left, right) => (left.getTime() <= right.getTime() ? left : right);
const floorToMinute = (instant) => Math.floor(instant.getTime() / MINUTE_MS) * MINUTE_MS;
/**
 * Le mezze giornate lavorative di un giorno locale, in ordine. Gli orari vengono dal calendario: un giorno che chiude
 * prima della fine della mattina ha soltanto la mattina, fino alla chiusura; uno che apre dopo l'inizio del pomeriggio
 * soltanto il pomeriggio, dall'apertura. Una mezza giornata vuota non c'è.
 */
export function halfDaysOf(day, calendar, rule) {
    const date = parseDay(day);
    const morningEnd = clockOf(rule.morningEnd, "morningEnd");
    const afternoonStart = clockOf(rule.afternoonStart, "afternoonStart");
    if (morningEnd.hour * 60 + morningEnd.minute > afternoonStart.hour * 60 + afternoonStart.minute) {
        throw new RangeError("morningEnd must not be after afternoonStart");
    }
    const window = workingWindowOf(date, calendar);
    if (!window)
        return [];
    const timeZone = calendar.timeZone;
    const halves = [
        { day, half: "morning", start: window.open, end: earlier(window.close, instantFromLocal(date, morningEnd, timeZone)) },
    ];
    if (!(rule.fridayOne && isoWeekday(date) === FRIDAY)) {
        halves.push({ day, half: "afternoon", start: later(window.open, instantFromLocal(date, afternoonStart, timeZone)), end: window.close });
    }
    return halves.filter((half) => half.start.getTime() < half.end.getTime());
}
/**
 * La mezza giornata in cui cade un istante o, se cade fuori (notte, pausa, venerdì pomeriggio, giorno chiuso), la prima
 * che viene dopo; `from` è da quando si conta (l'istante stesso se cade dentro, altrimenti l'inizio della mezza giornata).
 */
export function halfDayAt(instant, calendar, rule) {
    let date = localDateOf(instant, requireTimeZone(calendar?.timeZone));
    for (let index = 0, limit = searchLimitDays(calendar.closedDates?.length); index < limit; index += 1) {
        for (const half of halfDaysOf(dayString(date), calendar, rule)) {
            if (instant.getTime() < half.end.getTime()) {
                const inside = instant.getTime() >= half.start.getTime();
                return { half, from: inside ? instant : half.start, inside };
            }
        }
        date = addLocalDays(date, 1);
    }
    throw new RangeError("No working half day was found");
}
/** La mezza giornata lavorativa che viene dopo: quella in cui cade la fine di questa, o la prima dopo. */
export function nextHalfDay(half, calendar, rule) {
    return halfDayAt(half.end, calendar, rule).half;
}
/** Il nome di una mezza giornata: `2026-10-03:m` la mattina, `2026-10-03:p` il pomeriggio. Due nomi si confrontano come testo. */
export function halfDayKey(half) {
    return `${half.day}:${half.half === "morning" ? "m" : "p"}`;
}
/** La mezza giornata di un nome di `halfDayKey`, oppure `null` se il calendario quel giorno non la ha. */
export function halfDayFromKey(key, calendar, rule) {
    const match = /^((\d{4})-(\d{2})-(\d{2})):(m|p)$/.exec(typeof key === "string" ? key : "");
    if (!match?.[1] || !isCalendarDay({ year: Number(match[2]), month: Number(match[3]), day: Number(match[4]) }, match[1]))
        return null;
    const half = match[5] === "m" ? "morning" : "afternoon";
    return halfDaysOf(match[1], calendar, rule).find((item) => item.half === half) ?? null;
}
/** I minuti di lavoro fra due istanti: contano soltanto quelli dentro le mezze giornate. */
export function workMinutesBetween(from, to, calendar, rule) {
    const start = floorToMinute(from);
    const end = floorToMinute(to);
    if (end <= start)
        return 0;
    let half = halfDayAt(new Date(start), calendar, rule).half;
    let total = 0;
    while (half.start.getTime() < end) {
        total += Math.max(0, Math.min(half.end.getTime(), end) - Math.max(half.start.getTime(), start));
        half = nextHalfDay(half, calendar, rule);
    }
    return Math.round(total / MINUTE_MS);
}
/**
 * Entro quando va fatta la prima chiamata: `minutes` minuti di lavoro dall'ingresso del Lead, oppure dall'apertura della
 * mezza giornata dopo se il Lead entra fuori orario. Una chiamata nel minuto della scadenza è ancora in tempo, e il ritardo
 * comincia dal primo minuto di lavoro dopo: con la scadenza alla fine di una mezza giornata (alle 13:00) una chiamata alle
 * 14:00 è ancora in tempo. Il ritardo lo dice `sequenceVerdict`, non questo istante.
 */
export function firstCallDeadline(startedAt, minutes, calendar, rule) {
    if (!Number.isFinite(minutes) || minutes < 0)
        throw new RangeError("minutes must be a finite non-negative number");
    const begin = halfDayAt(new Date(floorToMinute(startedAt)), calendar, rule);
    let half = begin.half;
    let from = begin.from.getTime();
    let left = Math.round(minutes * MINUTE_MS);
    for (;;) {
        const available = half.end.getTime() - from;
        if (left <= available)
            return new Date(from + left);
        left -= available;
        half = nextHalfDay(half, calendar, rule);
        from = half.start.getTime();
    }
}
//# sourceMappingURL=call-sequence-calendar.js.map